import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { acceptsScope, useAuth, type LoginPortal } from "../model/AuthContext";
import { clearToken } from "@/shared/api";
import {
  DEFAULT_BRAND,
  ICP_BEIAN_URL,
  POLICE_BEIAN_URL,
  legalLink,
  pickBrandLogo,
  useBrand,
  type Brand,
} from "@/shared/brand";
import { useActiveSkin } from "@/shared/hooks/useTheme";
import {
  RETURN_TO_KEY,
  RETURN_TO_OWNER_KEY,
  landingFor,
  resolveReturnTo,
  savedReturnToFor,
} from "../model/returnTo";
import { loginErrorText, migratedLoginUrl } from "../model/loginErrorText";
import { KICKED_REASON_KEY, kickedMessageKey, normalizeKickedReason } from "../model/kickedReason";
import { Eye, EyeOff, Shield, Globe, Smartphone, Building2, Code2 } from "lucide-react";
import { Card, CardContent } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/ui/form";
import { ForcePasswordChangeDialog } from "./ForcePasswordChangeDialog";
import { LOGIN_NOTICE_KEY } from "@/shared/lib/passwordChangeRequired";
import { LoginBackdrop, LoginPipeline, LoginFxStyles } from "./LoginPipelineFx";

const loginSchema = z.object({
  credential: z.string().trim().min(1, "请输入账号 / 邮箱 / 手机号"),
  password: z.string().min(1, "请输入密码"),
});

type LoginValues = z.infer<typeof loginSchema>;

/** 协议链接:品牌外置了就新窗口外链,否则站内 /legal/*。 */
function LegalLink({
  brand,
  kind,
  label,
}: {
  brand: Brand;
  kind: "terms" | "privacy";
  label: string;
}) {
  const l = legalLink(brand, kind);
  return l.external ? (
    <a
      href={l.href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary hover:underline"
    >
      {label}
    </a>
  ) : (
    <Link to={l.href} className="text-primary hover:underline">
      {label}
    </Link>
  );
}

export default function Login() {
  const { t } = useTranslation("user");
  // OEM 贴牌(设计稿 §4):logo / 产品名 / 「登录{shortName}」/ 版权+ICP / 客服,全读品牌。
  // 直营 DEFAULT:无 logo、无 ICP、无客服行 → 与改造前逐字一致;NEUTRAL 兜底:不显示 logo 与版权。
  const brand = useBrand();
  const logoUrl = pickBrandLogo(brand, useActiveSkin() === "cyber");
  const navigate = useNavigate();
  const location = useLocation();
  const { login, logout, isAuthenticated, expectedScope, user } = useAuth();
  // OEM W3 C-G:登录口按 Host 选。非直营域名(品牌不是 DEFAULT = 后端认定的渠道,或渠道域名上品牌没拉到的
  // NEUTRAL)且本 entry 接受 PARTNER 时,给「渠道管理员登录」入口;直营域名上后端渠道口一律 401,不露入口。
  // 默认口:能登租户的 entry(index AUTO)默认企业账号;只收平台/渠道的 entry(platform.html)在渠道域名上
  // 默认就是渠道口(平台超管口在渠道域名上后端必 401)。?portal=partner 可直达渠道口(发给渠道的登录链接用)。
  const partnerEntry = acceptsScope(expectedScope, "PARTNER") && brand.key !== DEFAULT_BRAND.key;
  const [portal, setPortal] = useState<LoginPortal>(() => {
    if (!partnerEntry) return "default";
    const q = new URLSearchParams(location.search).get("portal");
    if (q === "partner") return "partner";
    return acceptsScope(expectedScope, "TENANT") ? "default" : "partner";
  });
  const isPartnerPortal = partnerEntry && portal === "partner";
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  // 1002:账号已改挂 / 渠道解约 → 后端(凭据通过后)给的新登录地址;有它就不显示泛泛的错误句。
  const [movedTo, setMovedTo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // 2026-08-09 首登强制改密:后端判定仍在用初始密码(0926 K 起=账号标记,存量 123456 兜底)→ 改完密码才放行进站。
  // 存登录时输入的凭据+旧密码(改密要 oldPassword;改密会踢会话,之后要用新密码自动重登),
  // 只在内存态,流程结束即清。
  const [forceChange, setForceChange] = useState<{
    credential: string;
    oldPassword: string;
    portal: LoginPortal;
  } | null>(null);

  const channels = [
    { icon: Globe, label: t("login.channelOfficial") },
    { icon: Smartphone, label: t("login.channelMiniProgram") },
    { icon: Building2, label: t("login.channelWeCom") },
    { icon: Code2, label: t("login.channelApi") },
  ];

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    mode: "onBlur",
    defaultValues: {
      credential: "",
      password: "",
    },
  });

  // BP-05:登录后回到被打断的地方(判据与 open-redirect 防线在 model/returnTo,单测锁死)
  // 1030:断线返回地址只给原主 —— 换账号登录时不跳进别人的页面(按登录后拿到的 userId 判)。
  const stateFrom = (location.state as { from?: { pathname: string } })?.from?.pathname;
  const fromFor = (userId: number | null | undefined) =>
    resolveReturnTo(
      stateFrom,
      savedReturnToFor(
        userId,
        sessionStorage.getItem(RETURN_TO_KEY),
        sessionStorage.getItem(RETURN_TO_OWNER_KEY)
      )
    );
  const from = fromFor(user?.userId);

  // 被踢下线(后端 401 SESSION_KICKED 被 httpClient 标记进 sessionStorage)。
  // B2 #6:按 reason 分句(禁用 / 公司停用 / 改密 / 别处登录 / 组织变更);没有 reason 说中性句 ——
  // 此前一律「已在别处登录」,被禁用的员工照着去改密码、查异地登录,方向全错。
  useEffect(() => {
    if (sessionStorage.getItem("xbgeo-session-kicked") === "1") {
      const reason = normalizeKickedReason(sessionStorage.getItem(KICKED_REASON_KEY));
      sessionStorage.removeItem("xbgeo-session-kicked");
      sessionStorage.removeItem(KICKED_REASON_KEY);
      setError(t(kickedMessageKey(reason)));
    }
    // 0930:刷新后在改密闸里改完密码 → 回登录页提示用新密码重登(一次性)。
    try {
      const notice = sessionStorage.getItem(LOGIN_NOTICE_KEY);
      if (notice) {
        sessionStorage.removeItem(LOGIN_NOTICE_KEY);
        setError(notice);
      }
    } catch {
      /* sessionStorage 不可用 */
    }
  }, [t]);

  // Redirect if already authenticated。两个例外都不拦不行:
  // ① 强制改密进行中(forceChangeOldPwd 非 null)—— token 已落地,跳走 = 绕过弹窗;
  // ② 登录请求还在途(loading)—— login() 内部 setUser 先触发本组件重渲,isAuthenticated
  //    已经是 true 而 setForceChangeOldPwd 还没来得及跑,这里先 Navigate 就把弹窗抢掉了
  //    (2026-08-09 真机走查抓到的竞态:带初始密码登录直接进站,强制改密一次都没弹)。
  if (isAuthenticated && !loading && forceChange === null) {
    // 用掉即清,否则下次主动登录还会被带回上次断线的页面
    sessionStorage.removeItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_OWNER_KEY);
    return <Navigate to={landingFor(user?.scope, from)} replace />;
  }

  const onSubmit = async (values: LoginValues) => {
    setError("");
    setMovedTo(null);
    setLoading(true);
    try {
      const usedPortal: LoginPortal = isPartnerPortal ? "partner" : "default";
      const outcome = await login(values.credential, values.password, usedPortal);
      if (outcome.requirePasswordChange) {
        setForceChange({
          credential: values.credential,
          oldPassword: values.password,
          portal: usedPortal,
        });
        return; // 改完密码再 navigate(见 ForcePasswordChangeDialog onDone)
      }
      // 按 scope 分流:渠道管理员 → /partner(除非 returnTo 本就在渠道后台内)
      navigate(landingFor(outcome.scope, fromFor(outcome.userId)), { replace: true });
    } catch (err) {
      const moved = migratedLoginUrl(err);
      if (moved) {
        setMovedTo(moved);
      } else {
        setError(loginErrorText(err, t("login.loginFailed")));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    /* 2026-08-27「作业流水线」改版(老板:登录页太空洞)。
       原版是一张 448px 的卡浮在空底上,整屏只有 8 个可读元素;这版把产品自己的五道工序
       搬上来当内容。视觉件在 ./LoginPipelineFx,零新依赖、全部走皮肤 token。
       ★表单逻辑一行没动 —— rhf+zod / 强制改密 / returnTo / 被挤下线,都还是上面那套。 */
    <div className="cyber-page relative flex min-h-screen flex-col overflow-hidden">
      <LoginFxStyles />
      <LoginBackdrop />

      {/* 上半:左叙事 + 右登录卡。窄屏单列(卡在下),lg 起分两栏。 */}
      <div className="relative z-10 mx-auto grid w-full max-w-[1440px] flex-1 grid-cols-1 items-start gap-10 px-6 pb-10 pt-12 sm:px-10 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16 lg:px-16 lg:pt-16">
        {/* lg 起对着登录卡垂直居中:卡片比左栏高得多,不居中的话左栏底下会空出一大条
            (0827 真机截图逮到的,计算属性看不出来)。 */}
        <div className="fx-enter min-w-0 lg:flex lg:flex-col lg:justify-center lg:self-stretch">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={brand.productName}
              className="mb-6 h-10 w-auto self-start object-contain"
            />
          )}
          <p className="text-[13px] font-medium uppercase tracking-[0.3em] text-primary">
            从一条线索到一单成交
          </p>

          {/* 标题:整串走渐变 + 扫光。
              ★千万别把它逐字拆开做动画 —— background-clip:text 的裁剪区**不含被 transform 的
                子元素**,一加 transform 整段就变成 color:transparent 的隐形字(0827 出稿时踩过,
                而 width/opacity/backgroundClip 三个读数在坏的那版上一模一样,判据全绿)。 */}
          <h1
            className="xb-shine mt-4 text-5xl font-semibold leading-[1.1] tracking-tight sm:text-6xl lg:text-[68px]"
            style={{
              backgroundImage: `linear-gradient(100deg,
                hsl(var(--foreground)) 0%, hsl(var(--foreground)) 38%,
                hsl(var(--gradient-hero-from)) 50%, hsl(var(--gradient-hero-to)) 57%,
                hsl(var(--foreground)) 69%, hsl(var(--foreground)) 100%)`,
              backgroundSize: "220% 100%",
              backgroundPosition: "0% 0",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("login.appName")}
          </h1>

          <p className="mt-4 max-w-[30ch] text-lg font-light leading-relaxed text-muted-foreground lg:text-xl">
            {brand.loginSlogan || t("login.tagline")}
          </p>

          {/* 四个入口 —— 复用既有的 channels(图标 + i18n 文案),不新造一套。 */}
          <div className="mt-10">
            <p className="text-base text-foreground/80">四个入口，一套账号</p>
            <ul className="mt-3.5 flex flex-wrap gap-2.5">
              {channels.map((ch) => (
                <li
                  key={ch.label}
                  className="flex items-center gap-2 rounded-full border border-border bg-card/60 px-4 py-1.5 text-sm text-muted-foreground"
                >
                  <ch.icon className="h-4 w-4 flex-none" />
                  {ch.label}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* 登录卡 */}
        <Card className="fx-enter w-full shadow-2xl lg:sticky lg:top-12">
          <CardContent className="p-7 sm:p-8">
            {isPartnerPortal ? (
              <>
                <h2 className="text-2xl font-semibold text-foreground">渠道管理员登录</h2>
                <p className="mt-2 text-sm text-muted-foreground">{brand.name} · 渠道后台</p>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-semibold text-foreground">登录{brand.shortName}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{brand.orgName}企业账号</p>
              </>
            )}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="mt-7 space-y-5">
                <FormField
                  control={form.control}
                  name="credential"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-sm text-muted-foreground">
                        {t("login.credentialLabel")}
                      </FormLabel>
                      <FormControl>
                        {/* h/字号由 twMerge 覆盖组件默认(h-10 / md:text-sm);16px 同时挡掉 iOS 聚焦自动放大 */}
                        <Input
                          className="h-[52px] md:text-base"
                          placeholder={t("login.credentialPlaceholder")}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-sm text-muted-foreground">
                        {t("login.passwordLabel")}
                      </FormLabel>
                      {/* relative 包 FormControl+button:FormControl(Slot)须直接包 Input,id 才落到
                          input(否则灌给 div → label htmlFor 关联断裂,A11Y-02)。 */}
                      <div className="relative">
                        <FormControl>
                          <Input
                            type={showPwd ? "text" : "password"}
                            placeholder={t("login.passwordPlaceholder")}
                            className="h-[52px] pr-11 md:text-base"
                            {...field}
                          />
                        </FormControl>
                        <button
                          type="button"
                          aria-label={showPwd ? t("login.hidePassword") : t("login.showPassword")}
                          onClick={() => setShowPwd(!showPwd)}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                        >
                          {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {error && (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}
                {movedTo && <AccountMovedNotice url={movedTo} />}

                <Button
                  type="submit"
                  disabled={loading}
                  className="h-[52px] w-full text-[17px] font-semibold tracking-[0.2em]"
                >
                  {loading ? t("login.submitting") : t("login.submit")}
                </Button>
                {/* 0926 修复波 #13(已拍板):没有自助找回 —— 口令由公司管理员在「用户管理」重置,
                    这里明说找谁,免得用户在登录页反复试错。 */}
                <p className="text-center text-[13px] text-muted-foreground">
                  {/* OEM F2:渠道管理员的口令由平台运营重置,不是「公司管理员」 */}
                  {t(isPartnerPortal ? "login.forgotPwdPartner" : "login.forgotPwd")}
                </p>
                {partnerEntry && (
                  <p className="text-center text-[13px]">
                    <button
                      type="button"
                      data-testid="login-portal-switch"
                      className="text-primary hover:underline"
                      onClick={() => {
                        setError("");
                        setPortal(isPartnerPortal ? "default" : "partner");
                      }}
                    >
                      {isPartnerPortal ? "返回企业账号登录" : "渠道管理员登录"}
                    </button>
                  </p>
                )}
                {(brand.support.text || brand.support.phone) && (
                  <p className="text-center text-[13px] text-muted-foreground">
                    {[brand.support.text, brand.support.phone].filter(Boolean).join(" ")}
                  </p>
                )}
              </form>
            </Form>

            <div className="mt-6 flex items-start gap-2 text-[13px] leading-relaxed text-muted-foreground">
              <Shield className="mt-0.5 h-4 w-4 flex-none" />
              <span>{t("login.securityTip")}</span>
            </div>
            <p className="mt-4 text-center text-[13px] leading-relaxed text-muted-foreground">
              登录即表示你已阅读并同意
              <LegalLink brand={brand} kind="terms" label="《用户服务协议》" />
              和
              <LegalLink brand={brand} kind="privacy" label="《隐私政策》" />
            </p>
          </CardContent>
        </Card>
      </div>

      {/* 下半:五道工序流水线 */}
      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 pb-10 sm:px-10 lg:px-16">
        <LoginPipeline />
        <p className="mt-8 text-center text-sm text-muted-foreground">
          登录之后，这条线上的每一步都在你自己的账号里跑
        </p>
        {(brand.copyright || brand.icp || brand.policeNo) && (
          <p className="mt-6 text-2xs text-muted-foreground/70">
            {/* 2026-05-25 F-8 修复:i18n hardcoded 2024,改 runtime 注入(现由品牌 copyright 的 {year} 占位承接) */}
            {brand.copyright && t("login.copyright")}
            {brand.icp && (
              <>
                {brand.copyright && " · "}
                <a
                  href={ICP_BEIAN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  {brand.icp}
                </a>
              </>
            )}
            {brand.policeNo && (
              <>
                {(brand.copyright || brand.icp) && " · "}
                <a
                  href={POLICE_BEIAN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  {brand.policeNo}
                </a>
              </>
            )}
          </p>
        )}
      </div>

      {forceChange !== null && (
        <ForcePasswordChangeDialog
          oldPassword={forceChange.oldPassword}
          onDone={async (newPassword) => {
            // 后端改密即踢会话(sid 重签)——旧 token 已死。api 客户端会给**所有**请求
            // 挂 Authorization(包括登录端点),带着死 token 去重登会被 JwtFilter 以
            // SESSION_KICKED 401 拦在 permitAll 之前(2026-08-09 真机抓到:401 仅 1ms,
            // 连 bcrypt 都没跑;curl 不带 header 就能登)。所以先清 token 再重登。
            clearToken();
            let reloginScope: string | undefined;
            try {
              reloginScope = (await login(forceChange.credential, newPassword, forceChange.portal))
                .scope;
            } catch {
              // 重登失败(网络抖动/频控):留在登录页,用户拿新密码手动登即可。
              // 0930:本地会话一并清掉 —— 否则 user 仍在、token 已清,会被当成已登录跳进站(撞改密闸)。
              logout();
              sessionStorage.removeItem("xbgeo-session-kicked");
              sessionStorage.removeItem(KICKED_REASON_KEY);
              setForceChange(null);
              setError("密码已更新,请用新密码重新登录");
              return;
            }
            sessionStorage.removeItem("xbgeo-session-kicked");
            sessionStorage.removeItem(KICKED_REASON_KEY);
            setForceChange(null);
            navigate(landingFor(reloginScope, from), { replace: true });
          }}
        />
      )}
    </div>
  );
}

/** 1002:账号已迁移(改挂到别的服务商 / 原服务商解约)→ 告诉用户新的登录地址。地址已在 migratedLoginUrl 里校验过协议。 */
function AccountMovedNotice({ url }: { url: string }) {
  const { t } = useTranslation("user");
  const host = (() => {
    try {
      return new URL(url).host;
    } catch {
      return url;
    }
  })();
  return (
    <div
      role="alert"
      data-testid="login-account-moved"
      className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm text-foreground"
    >
      <p>{t("login.accountMoved", { host })}</p>
      <a
        href={`${url}/login`}
        className="mt-1 inline-block font-medium text-primary underline-offset-2 hover:underline"
      >
        {t("login.accountMovedGo", { host })}
      </a>
    </div>
  );
}
